import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useRelay } from './useRelay';
import { Download, Play, Pause, SkipForward, SkipBack } from 'lucide-react';

/**
 * Finished phase — full canvas reveal + turn-by-turn playback.
 * 5-second "You made this together" hold before showing controls.
 */
export default function RelayFinished({ stageRef }) {
  const { state, dispatch } = useApp();
  const { game, user, canvas } = state;
  const { playAgain, endGame } = useRelay();
  const [showControls, setShowControls] = useState(false);
  const [playbackMode, setPlaybackMode] = useState(false);
  const [playbackTurn, setPlaybackTurn] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const originalStrokesRef = useRef(null);

  // 5-second reveal hold
  useEffect(() => {
    const timer = setTimeout(() => setShowControls(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Build turn info from turnHistory
  const turns = useMemo(() => {
    if (!game.turnHistory) return [];
    const entries = typeof game.turnHistory === 'object'
      ? Object.values(game.turnHistory)
      : game.turnHistory;
    return entries.sort((a, b) => (a.turnIndex || 0) - (b.turnIndex || 0));
  }, [game.turnHistory]);

  const maxTurnIndex = turns.length > 0 ? Math.max(...turns.map(t => t.turnIndex ?? 0)) : 0;

  // Playback: filter strokes by turnIndex and dispatch to canvas
  useEffect(() => {
    if (!playbackMode) return;
    if (playbackTurn < 0) {
      dispatch({ type: 'SET_REPLAY_STROKES', payload: [] });
      return;
    }
    const all = originalStrokesRef.current || canvas.allStrokes;
    const visible = all.filter(s => (s.turnIndex ?? 0) <= playbackTurn);
    dispatch({ type: 'SET_REPLAY_STROKES', payload: visible });
  }, [playbackMode, playbackTurn, dispatch]);

  // Auto-play through turns
  useEffect(() => {
    if (!isPlaying || !playbackMode) return;
    if (playbackTurn >= maxTurnIndex) {
      setIsPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      setPlaybackTurn(prev => prev + 1);
    }, 2000);
    return () => clearTimeout(timer);
  }, [isPlaying, playbackMode, playbackTurn, maxTurnIndex]);

  const startPlayback = useCallback(() => {
    // Save original strokes before playback
    originalStrokesRef.current = [...canvas.allStrokes];
    setPlaybackMode(true);
    setPlaybackTurn(-1);
    setIsPlaying(true);
    setTimeout(() => setPlaybackTurn(0), 500);
  }, [canvas.allStrokes]);

  const stopPlayback = useCallback(() => {
    setPlaybackMode(false);
    setIsPlaying(false);
    setPlaybackTurn(-1);
    // Restore original strokes
    if (originalStrokesRef.current) {
      dispatch({ type: 'RESTORE_STROKES', payload: originalStrokesRef.current });
      originalStrokesRef.current = null;
    }
  }, [dispatch]);

  // Export as PNG
  const handleExport = useCallback(() => {
    const stage = stageRef?.current;
    if (!stage) return;
    const dataUrl = stage.toDataURL({ pixelRatio: 2 });
    const link = document.createElement('a');
    link.download = `drawlab-relay-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  }, [stageRef]);

  // Current turn info for playback overlay
  const currentTurnInfo = turns.find(t => (t.turnIndex ?? 0) === playbackTurn);

  return (
    <>
      {/* Playback turn label overlay */}
      {playbackMode && currentTurnInfo && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-charcoal/80 backdrop-blur text-white px-4 py-2 rounded-full text-sm font-display">
            Turn {(currentTurnInfo.turnIndex ?? 0) + 1} — {currentTurnInfo.nickname}
          </div>
        </div>
      )}

      {/* "You made this together" moment */}
      {!showControls && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="text-center animate-[fadeIn_1s_ease-out]">
            <p className="text-3xl font-bold text-white font-display drop-shadow-lg">
              You made this together
            </p>
            {game.theme && (
              <p className="text-lg text-white/60 font-display mt-2 drop-shadow">
                {game.theme}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Controls overlay (after 5s hold) */}
      {showControls && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
          <div className="max-w-md mx-auto bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 space-y-3">
            {/* Playback controls */}
            <div className="flex items-center justify-center gap-2">
              {!playbackMode ? (
                <button
                  onClick={startPlayback}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-charcoal text-white text-sm font-display hover:bg-charcoal/80 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Watch it Build
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setPlaybackTurn(prev => Math.max(0, prev - 1))}
                    disabled={playbackTurn <= 0}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-30 transition-colors"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-charcoal text-white hover:bg-charcoal/80 transition-colors"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setPlaybackTurn(prev => Math.min(maxTurnIndex, prev + 1))}
                    disabled={playbackTurn >= maxTurnIndex}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-30 transition-colors"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                  <button
                    onClick={stopPlayback}
                    className="px-3 py-2 rounded-lg text-xs text-slate-400 font-display hover:text-slate-600 transition-colors"
                  >
                    Exit
                  </button>
                </>
              )}

              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-sm font-display hover:bg-slate-200 transition-colors"
              >
                <Download className="w-4 h-4" />
                Save PNG
              </button>
            </div>

            {/* Playback scrubber */}
            {playbackMode && (
              <div className="px-2">
                <input
                  type="range"
                  min={0}
                  max={maxTurnIndex}
                  value={playbackTurn}
                  onChange={(e) => {
                    setPlaybackTurn(Number(e.target.value));
                    setIsPlaying(false);
                  }}
                  className="w-full accent-amber"
                />
                <div className="flex justify-between text-xs text-slate-300 font-mono">
                  <span>Turn 1</span>
                  <span>Turn {maxTurnIndex + 1}</span>
                </div>
              </div>
            )}

            {/* Host actions */}
            {user.isHost && (
              <div className="flex gap-3 pt-2 border-t border-slate-100">
                <button
                  onClick={playAgain}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
                >
                  Play Again
                </button>
                <button
                  onClick={endGame}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  End Game
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
